import { useState, useMemo } from "react";
import {
  type AiSourceRule,
  type MatchOperator,
  type MatchableField,
  type RuleMatch,
  type RuleOverrides,
  useAiSourceRules,
  useCreateAiSourceRule,
  useUpdateAiSourceRule,
  useDeleteAiSourceRule,
} from "@/hooks/useAiAgent";
import { useCategories } from "@/hooks/useCategories";
import { useVenues } from "@/hooks/useVenues";
import { Button } from "@/components/ui/button";
import { ActionButton } from "@/components/ui/action-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const MATCHABLE_FIELDS: { value: MatchableField; label: string }[] = [
  { value: "title", label: "Tytuł" },
  { value: "description", label: "Opis" },
  { value: "locationName", label: "Nazwa miejsca" },
  { value: "locationAddress", label: "Adres" },
  { value: "sourceUrl", label: "URL źródła" },
];

type OperatorKey = "contains" | "startsWith" | "equals" | "regex";

const OPERATORS: { value: OperatorKey; label: string }[] = [
  { value: "contains", label: "Zawiera" },
  { value: "startsWith", label: "Zaczyna się od" },
  { value: "equals", label: "Dokładnie" },
  { value: "regex", label: "Regex" },
];

const REGISTRATION_TYPES: { value: "phone" | "email" | "url" | "other"; label: string }[] = [
  { value: "phone", label: "Telefon" },
  { value: "email", label: "Email" },
  { value: "url", label: "Link" },
  { value: "other", label: "Inne" },
];

type OverrideState = "unset" | "set" | "forceNull";

interface MatchRow {
  id: string;
  field: MatchableField;
  operator: OperatorKey;
  value: string;
}

interface FormState {
  name: string;
  enabled: boolean;
  priority: number;
  matchRows: MatchRow[];
  overrides: {
    suggestedCategory: { state: OverrideState; value: string };
    suggestedVenue: { state: OverrideState; value: string };
    registrationType: { state: OverrideState; value: string };
    registrationValue: { state: OverrideState; value: string };
    ticketPrice: { state: OverrideState; value: string };
    locationName: { state: OverrideState; value: string };
    locationAddress: { state: OverrideState; value: string };
  };
}

type OverrideKey = keyof FormState["overrides"];

function emptyForm(): FormState {
  return {
    name: "",
    enabled: true,
    priority: 0,
    matchRows: [],
    overrides: {
      suggestedCategory: { state: "unset", value: "" },
      suggestedVenue: { state: "unset", value: "" },
      registrationType: { state: "unset", value: "" },
      registrationValue: { state: "unset", value: "" },
      ticketPrice: { state: "unset", value: "" },
      locationName: { state: "unset", value: "" },
      locationAddress: { state: "unset", value: "" },
    },
  };
}

function ruleToForm(rule: AiSourceRule): FormState {
  const matchRows: MatchRow[] = Object.entries(rule.match).map(([field, op], i) => {
    const opEntries = Object.entries(op ?? {});
    const first = opEntries[0];
    return {
      id: `${i}`,
      field: field as MatchableField,
      operator: (first?.[0] as OperatorKey) ?? "contains",
      value: (first?.[1] as string) ?? "",
    };
  });

  const form = emptyForm();
  form.name = rule.name;
  form.enabled = rule.enabled;
  form.priority = rule.priority;
  form.matchRows = matchRows;

  const o = rule.overrides;
  const setField = (key: OverrideKey, value: unknown) => {
    if (value === undefined) return;
    if (value === null) {
      form.overrides[key] = { state: "forceNull", value: "" };
    } else {
      form.overrides[key] = { state: "set", value: String(value) };
    }
  };
  setField("suggestedCategory", o.suggestedCategory);
  setField("suggestedVenue", o.suggestedVenue);
  setField("registrationType", o.registrationType);
  setField("registrationValue", o.registrationValue);
  setField("ticketPrice", o.ticketPrice);
  setField("locationName", o.locationName);
  setField("locationAddress", o.locationAddress);

  return form;
}

function formToPayload(form: FormState): {
  name: string;
  enabled: boolean;
  priority: number;
  match: RuleMatch;
  overrides: RuleOverrides;
} {
  const match: RuleMatch = {};
  for (const row of form.matchRows) {
    if (!row.value && row.operator !== "equals") continue;
    const op = { [row.operator]: row.value } as MatchOperator;
    match[row.field] = op;
  }

  const overrides: RuleOverrides = {};
  const o = form.overrides;

  const str = (key: OverrideKey, set: (v: string | null) => void) => {
    const f = o[key];
    if (f.state === "forceNull") set(null);
    else if (f.state === "set") set(f.value);
  };

  str("suggestedCategory", (v) => (overrides.suggestedCategory = v));
  str("suggestedVenue", (v) => (overrides.suggestedVenue = v));
  str("registrationType", (v) => {
    overrides.registrationType = v as RuleOverrides["registrationType"];
  });
  str("registrationValue", (v) => (overrides.registrationValue = v));
  str("locationAddress", (v) => (overrides.locationAddress = v));

  if (o.locationName.state === "set") overrides.locationName = o.locationName.value;

  if (o.ticketPrice.state === "forceNull") overrides.ticketPrice = null;
  else if (o.ticketPrice.state === "set") {
    const n = Number(o.ticketPrice.value);
    if (!Number.isNaN(n)) overrides.ticketPrice = n;
  }

  return { name: form.name, enabled: form.enabled, priority: form.priority, match, overrides };
}

interface Props {
  sourceId: string | null;
  sourceName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AiSourceRulesDialog({ sourceId, sourceName, open, onOpenChange }: Props) {
  const { data: rules, isLoading } = useAiSourceRules(sourceId);
  const createRule = useCreateAiSourceRule(sourceId ?? "");
  const updateRule = useUpdateAiSourceRule(sourceId ?? "");
  const deleteRule = useDeleteAiSourceRule(sourceId ?? "");
  const { data: categories } = useCategories();
  const { data: venues } = useVenues();

  const [mode, setMode] = useState<"list" | "edit">("list");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setMode("edit");
  };

  const openEdit = (rule: AiSourceRule) => {
    setEditingId(rule.id);
    setForm(ruleToForm(rule));
    setMode("edit");
  };

  const handleSave = async () => {
    if (!sourceId || !form.name) return;
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await updateRule.mutateAsync({ id: editingId, ...payload });
        toast.success("Reguła zaktualizowana");
      } else {
        await createRule.mutateAsync(payload);
        toast.success("Reguła dodana");
      }
      setMode("list");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd zapisu");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRule.mutateAsync(id);
      toast.success("Reguła usunięta");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Błąd usuwania");
    }
  };

  const addMatchRow = () => {
    setForm((f) => ({
      ...f,
      matchRows: [
        ...f.matchRows,
        { id: crypto.randomUUID(), field: "title", operator: "contains", value: "" },
      ],
    }));
  };

  const updateMatchRow = (id: string, patch: Partial<MatchRow>) => {
    setForm((f) => ({
      ...f,
      matchRows: f.matchRows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  };

  const removeMatchRow = (id: string) => {
    setForm((f) => ({ ...f, matchRows: f.matchRows.filter((r) => r.id !== id) }));
  };

  const setOverride = (
    key: OverrideKey,
    patch: Partial<{ state: OverrideState; value: string }>,
  ) => {
    setForm((f) => ({
      ...f,
      overrides: { ...f.overrides, [key]: { ...f.overrides[key], ...patch } },
    }));
  };

  const sortedRules = useMemo(
    () => (rules ? [...rules].sort((a, b) => a.priority - b.priority) : []),
    [rules],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === "edit" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMode("list")}
                className="h-7 w-7"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            Reguły {sourceName ? `— ${sourceName}` : ""}
          </DialogTitle>
        </DialogHeader>

        {mode === "list" ? (
          <div className="space-y-3 py-2">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Ładowanie...</p>
            ) : sortedRules.length === 0 ? (
              <p className="text-sm text-muted-foreground">Brak reguł dla tego źródła</p>
            ) : (
              <ul className="space-y-2">
                {sortedRules.map((rule) => (
                  <li
                    key={rule.id}
                    className="flex items-center justify-between rounded border p-3"
                  >
                    <div>
                      <div className="font-medium text-sm">
                        {rule.name}
                        {!rule.enabled && (
                          <span className="ml-2 text-xs text-muted-foreground">(wyłączona)</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Priorytet: {rule.priority} · Warunków: {Object.keys(rule.match).length} ·
                        Zmian: {Object.keys(rule.overrides).length}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <ActionButton tooltip="Edytuj" onClick={() => openEdit(rule)}>
                        <Pencil className="h-4 w-4" />
                      </ActionButton>
                      <ActionButton tooltip="Usuń" onClick={() => handleDelete(rule.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </ActionButton>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Button onClick={openCreate} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Dodaj regułę
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label>Nazwa</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="np. Filmowe środy → Sztuka"
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <Label>Priorytet</Label>
                <Input
                  type="number"
                  value={form.priority}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">Wyższy wygrywa przy konflikcie</p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                  Aktywna
                </label>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Warunki dopasowania</Label>
                <Button variant="outline" size="sm" onClick={addMatchRow}>
                  <Plus className="h-3 w-3 mr-1" />
                  Dodaj warunek
                </Button>
              </div>
              {form.matchRows.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Brak warunków — reguła dopasuje każde wydarzenie
                </p>
              ) : (
                <div className="space-y-2">
                  {form.matchRows.map((row) => (
                    <div key={row.id} className="flex gap-2">
                      <Select
                        value={row.field}
                        onValueChange={(v) =>
                          updateMatchRow(row.id, { field: v as MatchableField })
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MATCHABLE_FIELDS.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={row.operator}
                        onValueChange={(v) =>
                          updateMatchRow(row.id, { operator: v as OperatorKey })
                        }
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATORS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={row.value}
                        onChange={(e) => updateMatchRow(row.id, { value: e.target.value })}
                        placeholder="wartość"
                        className="flex-1"
                      />
                      <ActionButton tooltip="Usuń warunek" onClick={() => removeMatchRow(row.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </ActionButton>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Zmiany do zastosowania</Label>
              <div className="space-y-2 mt-2">
                <OverrideSelectField
                  label="Kategoria"
                  state={form.overrides.suggestedCategory}
                  onChange={(patch) => setOverride("suggestedCategory", patch)}
                  options={(categories ?? []).map((c) => ({ value: c.name, label: c.name }))}
                  placeholder="Wybierz kategorię..."
                />
                <OverrideSelectField
                  label="Miejsce"
                  state={form.overrides.suggestedVenue}
                  onChange={(patch) => setOverride("suggestedVenue", patch)}
                  options={(venues ?? []).map((v) => ({ value: v.name, label: v.name }))}
                  placeholder="Wybierz miejsce..."
                />
                <OverrideSelectField
                  label="Typ rejestracji"
                  state={form.overrides.registrationType}
                  onChange={(patch) => setOverride("registrationType", patch)}
                  options={REGISTRATION_TYPES.map((r) => ({ value: r.value, label: r.label }))}
                  placeholder="Wybierz..."
                />
                <OverrideTextField
                  label="Wartość rejestracji"
                  state={form.overrides.registrationValue}
                  onChange={(patch) => setOverride("registrationValue", patch)}
                />
                <OverrideTextField
                  label="Cena biletu"
                  state={form.overrides.ticketPrice}
                  onChange={(patch) => setOverride("ticketPrice", patch)}
                  type="number"
                />
                <OverrideTextField
                  label="Nazwa miejsca"
                  state={form.overrides.locationName}
                  onChange={(patch) => setOverride("locationName", patch)}
                  noNull
                />
                <OverrideTextField
                  label="Adres"
                  state={form.overrides.locationAddress}
                  onChange={(patch) => setOverride("locationAddress", patch)}
                />
              </div>
            </div>
          </div>
        )}

        {mode === "edit" && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setMode("list")}>
              Anuluj
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.name || createRule.isPending || updateRule.isPending}
            >
              {editingId ? "Zapisz" : "Dodaj"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface OverrideFieldProps {
  label: string;
  state: { state: OverrideState; value: string };
  onChange: (patch: Partial<{ state: OverrideState; value: string }>) => void;
}

function StateToggle({
  state,
  onChange,
  allowNull = true,
}: {
  state: OverrideState;
  onChange: (s: OverrideState) => void;
  allowNull?: boolean;
}) {
  return (
    <Select value={state} onValueChange={(v) => onChange(v as OverrideState)}>
      <SelectTrigger className="w-[120px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unset">Nie zmieniaj</SelectItem>
        <SelectItem value="set">Ustaw</SelectItem>
        {allowNull && <SelectItem value="forceNull">Wyczyść</SelectItem>}
      </SelectContent>
    </Select>
  );
}

function OverrideTextField({
  label,
  state,
  onChange,
  type = "text",
  noNull = false,
}: OverrideFieldProps & { type?: "text" | "number"; noNull?: boolean }) {
  return (
    <div className="grid grid-cols-[140px_120px_1fr] gap-2 items-center">
      <Label className="text-sm">{label}</Label>
      <StateToggle
        state={state.state}
        onChange={(s) => onChange({ state: s })}
        allowNull={!noNull}
      />
      <Input
        type={type}
        value={state.value}
        onChange={(e) => onChange({ value: e.target.value })}
        disabled={state.state !== "set"}
        placeholder={state.state === "set" ? "" : "—"}
      />
    </div>
  );
}

function OverrideSelectField({
  label,
  state,
  onChange,
  options,
  placeholder,
}: OverrideFieldProps & { options: { value: string; label: string }[]; placeholder: string }) {
  return (
    <div className="grid grid-cols-[140px_120px_1fr] gap-2 items-center">
      <Label className="text-sm">{label}</Label>
      <StateToggle state={state.state} onChange={(s) => onChange({ state: s })} />
      <Select
        value={state.value}
        onValueChange={(v) => onChange({ value: v })}
        disabled={state.state !== "set"}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
